import BrandLogo from '@components/BrandLogo/BrandLogo'
import {
	faCircleDollarToSlot,
	faDollar,
	faEuroSign,
	faFrancSign,
	faPiggyBank,
	faPuzzle,
	faSackDollar,
	faSterlingSign,
	faSwedishKronaSign,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Chip, Divider, Paper, Skeleton, Stack, useTheme } from '@mui/material'
import MarketBasketsApi, { MarketBasketsKeys } from '@services/marketBaskets.api'
import { useQuery } from '@tanstack/react-query'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'
import { useParams } from 'react-router-dom'

import KPI from './KPI'

const currencyIcons = {
	CAD: faDollar,
	CHF: faFrancSign,
	EUR: faEuroSign,
	GBP: faSterlingSign,
	SEK: faSwedishKronaSign,
	USD: faDollar,
}

export default function KPIBar() {
	const { marketBasketId } = useParams()
	const { marketBasket } = useMarketBasketContext()
	const theme = useTheme()

	const { data, isLoading } = useQuery({
		queryKey: MarketBasketsKeys.kpis(marketBasketId),
		queryFn: () => MarketBasketsApi.kpis(marketBasketId),
	})

	const currency = marketBasket?.currency || 'USD'
	const currencyIcon = currencyIcons[currency]

	if (isLoading) {
		return (
			<Paper elevation={0} sx={{ height: '2.5rem', width: 'fit-content', minWidth: 'fit-content' }}>
				<Stack p={2} direction="row" gap={3} height="100%" alignItems="center">
					<Skeleton width="10rem" />
					<Skeleton width="10rem" />
					<Skeleton width="10rem" />
					<Skeleton width="10rem" />
					<Skeleton width="10rem" />
				</Stack>
			</Paper>
		)
	}

	return (
		<Paper elevation={0} sx={{ height: '2.5rem', width: '100%', minWidth: 'fit-content' }}>
			<Stack gap={1} alignItems="center" sx={{ height: '100%' }} direction="row">
				<BrandLogo brand={marketBasket?.bepBrand} themeMode={theme.palette.mode} />
				<Divider orientation="vertical" />

				{/* Total Spend */}
				<KPI
					icon={<FontAwesomeIcon size="lg" icon={currencyIcon} />}
					label="Annual Total Spend"
					value={data.totalSpend}
				/>
				<Divider orientation="vertical" />

				{/* Matched */}
				<KPI
					icon={<FontAwesomeIcon size="lg" icon={faCircleDollarToSlot} />}
					label="Matched"
					value={data.matchedSpend}
					chip={
						<Chip
							size="small"
							color={data.matchedSpendPct > 0 ? 'success' : 'error'}
							label={`${data.matchedSpendPct !== null ? data.matchedSpendPct : '--'}%`}
						/>
					}
				/>
				<Divider orientation="vertical" />

				{/* Savings */}
				<KPI
					icon={<FontAwesomeIcon size="lg" icon={faPiggyBank} />}
					label="Savings"
					value={data.savings}
					chip={
						<Chip
							size="small"
							color={data.savingsPct > 0 ? 'success' : 'error'}
							label={`${data.savingsPct !== null ? data.savingsPct : '--'}%`}
						/>
					}
				/>
				<Divider orientation="vertical" />

				{/* Exact Matches */}
				<KPI
					icon={<FontAwesomeIcon size="lg" icon={faPuzzle} />}
					label="Exact Matches"
					value={data.exactMatchSavings}
					currency={currency}
					details={[
						{
							label: 'Price Savings',
							value: data.exactMatchPriceSavings,
							percentage: data.exactMatchPriceSavingsPct,
						},
						{
							label: 'Rebate Savings',
							value: data.exactMatchRebates,
							percentage: data.exactMatchRebatesPct,
						},
					]}
					chip={
						<Chip
							size="small"
							color={data.exactMatchSavings > 0 ? 'success' : 'error'}
							label={`${data.exactMatchSavingsPct !== null ? data.exactMatchSavingsPct : '--'}%`}
						/>
					}
				/>
				<Divider orientation="vertical" />

				{/* Similar Matches */}
				<KPI
					icon={<FontAwesomeIcon size="lg" icon={faSackDollar} />}
					label="Similar Matches"
					value={data.similarMatchSavings}
					currency={currency}
					details={[
						{
							label: 'Price Savings',
							value: data.similarMatchPriceSavings,
							percentage: data.similarMatchPriceSavingsPct,
						},
						{
							label: 'Rebate Savings',
							value: data.similarMatchRebates,
							percentage: data.similarMatchRebatesPct,
						},
					]}
					chip={
						<Chip
							size="small"
							color={data.similarMatchSavingsPct > 0 ? 'success' : 'error'}
							label={`${data.similarMatchSavingsPct !== null ? data.similarMatchSavingsPct : '--'}%`}
						/>
					}
				/>
			</Stack>
		</Paper>
	)
}
